import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { Ionicons } from '@expo/vector-icons';
import type { UIMessage } from 'ai';
import React, { memo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { LegendList } from '@legendapp/list';
import { ChatEmptyState } from './ChatEmptyState';
import { ChatMessageBubble } from './ChatMessageBubble';
import type { VoiceActionItem } from '@/types/voice-transcription';

interface ChatConversationViewProps {
  messages: UIMessage[];
  accentColor: string;
  textColor: string;
  userName?: string;
  error?: Error | null;
  usageLimitError?: string | null;
  /** Height of the top blur zone — used as top padding so content clears the buttons. */
  topInset?: number;
  onVoiceActionAddTask?: (item: VoiceActionItem) => void;
  onVoiceActionAddEvent?: (item: VoiceActionItem) => void;
  onReport?: (message: UIMessage) => void;
  onStarterPromptPress?: (prompt: string) => void;
}

export const ChatConversationView = memo(function ChatConversationView({
  messages,
  accentColor,
  textColor,
  userName = 'User',
  error = null,
  usageLimitError = null,
  topInset = 0,
  onVoiceActionAddTask,
  onVoiceActionAddEvent,
  onReport,
  onStarterPromptPress,
}: ChatConversationViewProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const renderItem = ({ item, index }: { item: UIMessage; index: number }) => (
    <ChatMessageBubble
      message={item}
      accentColor={accentColor}
      textColor={textColor}
      userName={userName}
      isLatestMessage={index === messages.length - 1}
      onVoiceActionAddTask={onVoiceActionAddTask}
      onVoiceActionAddEvent={onVoiceActionAddEvent}
      onReport={onReport}
    />
  );

  const renderFooterComponent = () => {
    if (!error && !usageLimitError) return null;

    return (
      <View style={styles.footer}>
        {error && (
          <View style={styles.errorBox}>
            <Ionicons name="warning-outline" size={16} color={palette.danger} />
            <Text style={[styles.errorText, { color: palette.danger }]}>
              {error.message}
            </Text>
          </View>
        )}

        {usageLimitError && (
          <View style={[styles.errorBox, styles.usageLimitBox]}>
            <Ionicons name="alert-circle-outline" size={20} color={palette.danger} />
            <View style={styles.usageLimitContent}>
              <Text style={[styles.errorText, { color: palette.danger, fontWeight: '600' }]}>
                Usage Limit Reached
              </Text>
              <Text style={[styles.usageLimitText, { color: palette.textMuted }]}>
                {usageLimitError}
              </Text>
            </View>
          </View>
        )}
      </View>
    );
  };

  if (messages.length === 0) {
    return (
      <View style={styles.list}>
        <ChatEmptyState
          accentColor={accentColor}
          textColor={textColor}
          onPromptPress={onStarterPromptPress}
        />
      </View>
    );
  }

  return (
    <View style={styles.list}>
      <LegendList
        data={messages}
        renderItem={renderItem}
        keyExtractor={item => item.id}
        alignItemsAtEnd={true}
        maintainScrollAtEnd={true}
        recycleItems={true}
        style={styles.list}
        contentContainerStyle={[styles.content, { paddingTop: topInset }]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        ListFooterComponent={renderFooterComponent}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  list: {
    flex: 1,
  },
  content: {
    paddingHorizontal: Spacing.lg,
    paddingBottom: Spacing.sm,
  },
  footer: {
    gap: Spacing.md,
    paddingTop: Spacing.sm,
  },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderRadius: BorderRadius.md,
    backgroundColor: 'rgba(241, 91, 181, 0.12)',
  },
  errorText: {
    fontSize: 12,
  },
  usageLimitBox: {
    paddingVertical: Spacing.lg,
    paddingHorizontal: Spacing.xl,
  },
  usageLimitContent: {
    flex: 1,
    gap: Spacing.xs,
  },
  usageLimitText: {
    fontSize: 12,
    lineHeight: 18,
  },
});
